import { useEffect, useState } from "react";
import { Camera as CameraIcon } from "lucide-react";
import { imageRotationClass } from "utils/cameraImage";

const formatBytes = (bytes) =>
  bytes ? `${(bytes / 1024).toFixed(1)} KiB` : "—";

// The stand-in for a photo that was just requested: shows the camera is working, and how long it has
// taken. Ticks on its own timer so the rest of the page does not re-render every second.
function PendingCaptureTile({ startedAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return (
    <div
      role="status"
      data-testid="pending-capture"
      className="overflow-hidden rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50 text-left"
    >
      <div className="flex aspect-[4/3] flex-col items-center justify-center gap-3 px-4 text-center text-blue-800">
        <CameraIcon className="h-8 w-8 animate-pulse" />
        <p className="text-sm font-bold">Capturing…</p>
        <p className="text-xs">
          Request sent. Waiting for the camera to take the photo and send it over (usually under 20 s).
        </p>
      </div>
      <div className="p-3 text-xs font-semibold text-blue-700">Waiting {seconds}s</div>
    </div>
  );
}

export default function CameraGallery({
  images,
  thumbnailUrls,
  onOpen,
  onLoadMore,
  hasMore,
  loadingMore,
  pendingCapture = null,
  highlightImageId = null,
}) {
  const waiting = pendingCapture?.phase === "waiting";
  if (!images.length && !waiting) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
        No durably stored images are associated with this run yet.
      </div>
    );
  }

  return (
    <section>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {waiting && <PendingCaptureTile startedAt={pendingCapture.startedAt} />}
        {images.map((image) => (
          <button
            key={image.id}
            onClick={() => onOpen(image)}
            data-testid={image.id === highlightImageId ? "new-capture" : undefined}
            className={`overflow-hidden rounded-2xl border bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
              image.id === highlightImageId ? "border-emerald-500 ring-2 ring-emerald-400" : "border-slate-200"
            }`}
          >
            <div className="aspect-[4/3] bg-slate-100">
              {thumbnailUrls[String(image.id)] ? (
                <img
                  src={thumbnailUrls[String(image.id)]}
                  alt={`Ruler capture ${image.capture_id}`}
                  className={`h-full w-full object-contain ${imageRotationClass(image.rotation_deg)}`}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  Loading protected thumbnail…
                </div>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-bold">
                  #{image.capture_id}
                  {image.id === highlightImageId && (
                    <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">New</span>
                  )}
                </span>
                <span className="text-xs text-slate-500">{formatBytes(image.jpeg_bytes)}</span>
              </div>
              <p className="mt-1 text-sm text-slate-700">
                {image.captured_at ? new Date(image.captured_at).toLocaleString() : "Device time unavailable"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {image.capture_trigger || "unknown trigger"} · {image.wake_reason || "unknown wake"}
              </p>
            </div>
          </button>
        ))}
      </div>
      {hasMore && (
        <div className="mt-5 text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700"
          >
            {loadingMore ? "Loading…" : "Load older images"}
          </button>
        </div>
      )}
    </section>
  );
}
